import { inject, Component, Inject, ModelSignal, OnChanges, SimpleChanges, effect, input, model } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ConsultorioEspecialidadeFeatureService } from './consultorio-especialidade.service';
import { ConsultorioEspecialidadeRequest } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-especialidade.model';
import { finalize } from 'rxjs/operators';
import { BotaoAcoesType } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { ActionChildComponent } from '../../../../../../../shared/components/containers/action-child.component';
import { MenuItem } from 'primeng/api';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

interface EspecialidadeItem {
    especialidade: string;
    areaAtuacao: string;
    dataInicio: string; // dd/MM/yyyy
    dataFim: string; // dd/MM/yyyy
}

@Component({
    selector: 'app-consultorio-especialidade',
    standalone: true,
    imports: [ButtonModule, TooltipModule, CardModule, CustomTableComponent],
    templateUrl: './consultorio-especialidade.html',
    styleUrl: './consultorio-especialidade.scss'
})
export class ConsultorioEspecialidadeComponent extends ActionChildComponent implements OnChanges {
    private readonly logger = inject(LoggerService);
    selected: ModelSignal<EspecialidadeItem | undefined> = model();
    protected readonly roleConsultorioEditar = PERMISSOES.medicos.consultorio.editar;

    constructor(
        @Inject(ConsultorioEspecialidadeFeatureService)
        private feature: ConsultorioEspecialidadeFeatureService
    ) {
        super();
        effect(() => {
            this.selected();
            this.updateActions();
        });
    }

    tableMetadata: TableMetadata = {
        headers: {
            especialidade: { type: 'text', value: 'Especialidade', hidden: false, default: '' },
            areaAtuacao: { type: 'text', value: 'Área de Atuação', hidden: false, default: '' },
            dataInicio: { type: 'text', value: 'Data Início', hidden: false, default: '' },
            dataFim: { type: 'text', value: 'Data Fim', hidden: false, default: '' }
        },
        title: 'especialidade',
        actions: {
            remover: {
                label: '',
                icon: 'pi pi-trash',
                color: 'danger',
                permission: this.roleConsultorioEditar,
                command: (item: unknown) => this.deletar(item)
            }
        }
    };

    pessoa = input<Pessoa>();
    idConsultorio = input<number | null>(null);

    // TO-DO: Remover mocks
    especialidades: EspecialidadeItem[] = [{ especialidade: 'test', areaAtuacao: 'teste', dataInicio: '12/03/1978', dataFim: '05/08/2012' }];

    // Estados
    loading = false;
    erro?: string;
    actions: BotaoAcoesType & { useEdit: boolean } = {
        items: [
            {
                label: 'Adicionar',
                icon: 'pi pi-plus-circle',
                command: () => this.novo(),
                styleClass: 'bg-green-500',
                permission: this.roleConsultorioEditar
            }
        ],
        useEdit: false
    };

    override initComponent(): void {
        this.setToggleableActions(new Set<string>(Array.of(this.actions.items?.[1]?.label ?? '')));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa'] || changes['idConsultorio']) {
            const p = this.pessoa();
            const idC = this.idConsultorio();
            if (p && p.id !== undefined && p.id !== null && !!p.uf && typeof idC === 'number') {
                this.carregar({ id: p.id, uf: p.uf, idConsultorio: idC });
            } else {
                this.especialidades = [];
            }
        }
    }

    private carregar(params: Pessoa & { idConsultorio: number }): void {
        this.loading = true;
        this.erro = undefined;
        this.feature
            .buscar(params)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (lista: ConsultorioEspecialidadeRequest[] | null | undefined) => {
                    this.especialidades = (lista ?? []).map(
                        (r: ConsultorioEspecialidadeRequest): EspecialidadeItem => ({
                            especialidade: r.dsEspecialidade ?? '',
                            areaAtuacao: r.dsAreaAtuacao ?? '',
                            dataInicio: this.formatarData(r.dtInicio),
                            dataFim: this.formatarData(r.dtFim)
                        })
                    );
                },
                error: (err: unknown) => {
                    this.logger.error('Erro ao buscar especialidades do consultório', err);
                    this.especialidades = [];
                    this.erro = 'Falha ao carregar especialidades do consultório.';
                }
            });
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            if (isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return '';
        }
    }

    /**
     * TO-DO: Implementar novo especialidade
     */
    novo() {
        this.logger.info('NOVO Não implementado! Implementar na API e plugar no front end');
        this.selected.set(undefined);
    }

    /**
     * TO-DO: Implementar deletar especialidade
     */
    deletar(item?: unknown) {
        this.logger.info('DELETAR', item);
        this.selected.set(undefined);
    }

    override getDefinedActions(): BotaoAcoesType & { useEdit: boolean } {
        return this.actions;
    }

    override toggleAction(action: MenuItem): MenuItem {
        action.disabled = this.selected() === undefined;
        return action;
    }

    override updateActionsWithIndexes(actions: [MenuItem, number][]): void {
        actions.forEach(([action, index]) => {
            this.actions.items[index] = action;
        });
    }
}
